/**
 * Shared "Hybrid" theme tokens + primitive components used across every public
 * page (PublicLanding, IndustryLanding, Pricing, Directory, Industries,
 * OnboardingPublic, PrivacyPolicy, Terms).
 *
 * Palette: light warm cream base + deep navy ink + coral primary + brass secondary + sage success.
 * Type:    Fraunces (display serif) + Inter (body) + JetBrains Mono (accents).
 */
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink } from "lucide-react";

export const T = {
  // Dark-first AI-modern palette (matched to /PublicLanding)
  cream:     "#0A0E1A",   // page background (deep space navy)
  canvas:    "#0E1420",   // alternating section background
  panel:     "#141B2D",   // card / panel surface
  ink:       "#F5F7FA",   // primary text (near-white)
  inkSoft:   "#D1D9E6",
  inkMuted:  "#8B95A7",
  inkFaint:  "#5A6478",
  coral:     "#FF6B47",   // primary accent (hot coral)
  coralDark: "#E5533B",
  coralSoft: "#3A1F1A",
  brass:     "#F5B840",   // secondary accent (bright brass)
  brassSoft: "#2A2416",
  sage:      "#4ADE80",   // success (bright mint on dark)
  line:      "#1E293B",
  lineSoft:  "#141B2D",
};

export const FONT_BODY    = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";
export const FONT_DISPLAY = "'Fraunces', 'Playfair Display', Georgia, serif";
export const FONT_MONO    = "'JetBrains Mono', 'IBM Plex Mono', monospace";

export const container = () => ({ maxWidth: 1200, margin: "0 auto", padding: "0 28px" });
export const eyebrow   = () => ({ fontSize: 11, fontFamily: FONT_MONO, letterSpacing: "0.16em", textTransform: "uppercase", color: T.coral, fontWeight: 600 });
export const h2        = () => ({ fontFamily: FONT_DISPLAY, fontSize: 60, letterSpacing: "-0.03em", lineHeight: 1.05, fontWeight: 400, margin: "16px 0 0", color: T.ink });
export const italicAccent = (color) => ({ fontStyle: "italic", color, fontWeight: 400 });
export const navLinkStyle = () => ({ color: T.inkMuted, textDecoration: "none", background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: 14, fontFamily: "inherit" });
export const btnCoral  = () => ({ background: T.coral, color: "#FFFFFF", border: "none", padding: "11px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 });
export const btnInk    = () => ({ background: T.ink, color: "#0A0E1A", border: "none", padding: "11px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 });
export const btnGhost  = () => ({ background: "transparent", color: T.ink, border: `1px solid ${T.line}`, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" });
export const input     = () => ({ background: T.panel, color: T.ink, border: `1px solid ${T.line}`, padding: "12px 14px", fontSize: 14, fontFamily: "inherit", outline: "none", width: "100%" });

export function PageShell({ children, testId }) {
  return (
    <div data-testid={testId} style={{ background: T.cream, color: T.ink, fontFamily: FONT_BODY, minHeight: "100vh" }}>
      {children}
    </div>
  );
}

export function TopNav({ activeKey }) {
  const items = [
    { key: "how",         label: "How it works",  to: "/#how" },
    { key: "industries",  label: "Industries",    to: "/industries" },
    { key: "ai_visibility", label: "AI Visibility", to: "/ai-visibility" },
    { key: "see_demo",    label: "See a demo",    to: "/preview/hybrid" },
    { key: "pricing",     label: "Pricing",       to: "/pricing-public" },
    { key: "onboarding",  label: "Onboarding",    to: "/onboarding-public" },
  ];
  return (
    <nav style={{ borderBottom: `1px solid ${T.line}`, background: "rgba(10,14,26,0.85)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", position: "sticky", top: 0, zIndex: 30 }}>
      <div style={container()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 76 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} data-testid="public-brand-logo">
            <img
              src="/logo-on-dark.png"
              alt="Clients OnRamp"
              style={{ height: 52, width: "auto", display: "block", objectFit: "contain" }}
            />
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 28, fontSize: 14 }}>
            {items.map((it) => (
              <Link key={it.key} to={it.to} data-testid={`nav-${it.key}`} style={{
                ...navLinkStyle(),
                color: activeKey === it.key ? T.coral : T.inkMuted,
                fontWeight: activeKey === it.key ? 600 : 400,
              }}>
                {it.label}
              </Link>
            ))}
            <a
              href="tel:+15625743752"
              data-testid="nav-tel"
              style={{ ...navLinkStyle(), color: T.ink, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
            >
              📞 (562) 574-3752
            </a>
        <Link to="/book" data-testid="nav-book-demo" style={{ ...btnCoral(), textDecoration: "none", whiteSpace: "nowrap" }}>
              Book demo <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}

export function Footer() {
  const link = { color: T.inkMuted, textDecoration: "none" };
  return (
    <footer style={{ borderTop: `1px solid ${T.lineSoft}`, padding: "56px 0", fontSize: 13, color: T.inkMuted, background: T.panel }}>
      <div style={container()}>
        <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 24, alignItems: "center" }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", textDecoration: "none" }}>
            <img
              src="/logo-on-dark.png"
              alt="Clients OnRamp"
              style={{ height: 42, width: "auto", display: "block", objectFit: "contain" }}
            />
          </Link>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <Link to="/preview/hybrid" target="_blank" rel="noopener" style={link}>See a demo site</Link>
            <Link to="/industries" style={link}>Industries</Link>
            <Link to="/pricing-public" style={link}>Pricing</Link>
            <Link to="/onboarding-public" style={link}>Onboarding</Link>
            <Link to="/privacy" style={link}>Privacy</Link>
            <Link to="/terms" style={link}>Terms</Link>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 12, color: T.inkFaint, fontFamily: FONT_MONO }}>
            <Link to="/login" data-testid="footer-admin-link" style={{ color: T.inkFaint, textDecoration: "none", opacity: 0.55 }}>Admin</Link>
            <span>© 2026 Clients OnRamp</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

/** Compact hero used by simpler pages (Directory, Industries, Legal etc.). */
export function PageHero({ eyebrow: eb, title, titleAccent, subtitle, children }) {
  return (
    <section style={{ padding: "96px 0 56px", position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: -80, right: -120, width: 500, height: 500, background: "radial-gradient(circle, rgba(229,83,59,0.06) 0%, transparent 60%)", pointerEvents: "none", filter: "blur(50px)" }} />
      <div style={container()}>
        <div style={{ maxWidth: 900, position: "relative" }}>
          {eb && <div style={eyebrow()}>{eb}</div>}
          <h1 style={{
            fontFamily: FONT_DISPLAY, fontSize: "clamp(48px, 6vw, 88px)",
            lineHeight: 1.02, letterSpacing: "-0.03em", fontWeight: 400,
            margin: "20px 0 20px", color: T.ink,
          }}>
            {title}{titleAccent && <> <em style={italicAccent(T.coral)}>{titleAccent}</em></>}
          </h1>
          {subtitle && <p style={{ fontSize: 20, lineHeight: 1.55, color: T.inkMuted, maxWidth: 640, margin: 0 }}>{subtitle}</p>}
          {children}
        </div>
      </div>
    </section>
  );
}

/** External-link icon export for convenience */
export { ExternalLink };
